import React from 'react';
import Article from '../models/Article';
import ReactHtmlParser, { Transform, convertNodeToElement } from 'react-html-parser';
import { DomElement } from 'htmlparser2';
import GalleryWithLightBox from './GalleryWithLightBox';
import ArticleComponentCredits from './ArticleComponentCredits';
import RecipeCard from './RecipeCard';
import ProductCarousel from './ProductCarousel';
import BuyingGuide from './BuyingGuide';

interface ArticleBodyProps {
  article: Article;
}

const maxGalleries = 3;

export default class ArticleBody extends React.PureComponent<ArticleBodyProps> {
  render() {
    let html = '<div id="article-body" class="article-body">';
    html += this.props.article.content;

    // add leading style to the first paragraph
    html = html.replace('<p>', '<p class="lead">');

    // add specific class to all WP images
    html = html.replace(/<img.+?class="/g, match => match + 'wp-image ');

    // add specific class to all WP links
    html = html.replace(/<a href=/g, '<a class="wp-link" href=');

    // shortcode replacement
    html = html.replace(/\[masonry-gallery ([\s\S]+?)\]/g, '<wp-gallery $1></wp-gallery>');
    html = html.replace(/\[youtube ([\s\S]+?)\]/g, '<wp-youtube $1></wp-youtube>');
    html = html.replace(
      /\[block-quote ([\s\S]+?)\]([\s\S]+?)\[\/block-quote\]/g,
      '<wp-blockquote $1>$2</wp-blockquote>'
    );
    html = html.replace(/\[recipe\]/g, '<wp-recipe></wp-recipe>');
    html = html.replace(/\[product-bundle([\s\S]+?)\]/g, '<wp-product-bundle$1></wp-product-bundle>');
    html = html.replace(/\[buying-guide\]/g, '<wp-buying-guide></wp-buying-guide>');

    // remove WP-generated slashes that kill shortcode attributess
    // TODO: Replace within the shortcodes rather than the whole article? or is there a WP solution?
    html = html.replace(/&#8221;/g, '"');
    html = html.replace(/&#8243;/g, '"');

    // Inject mandatory components at the bottom of the post if they haven't been added as shortcodes
    // [Recipe, Helpful Products, Buying Guide, Galleries(1,2,3)]
    // TODO: Helpful products and buying guide
    if (
      this.props.article.recipe &&
      html.indexOf('<wp-recipe>') < 0 &&
      this.props.article.customContentLayoutTypes.indexOf('recipe') >= 0
    ) {
      html += '<wp-recipe></wp-recipe>';
    }
    for (let i = 1; i <= maxGalleries; i++) {
      if (
        this.props.article.galleries.length >= i &&
        html.indexOf(`<wp-gallery index="${i}"`) < 0 &&
        this.props.article.customContentLayoutTypes.indexOf('galleries') >= 0
      ) {
        html += `<wp-gallery index="${i}"></wp-gallery>`;
      }
    }
    if (this.props.article.productBundle && html.indexOf('<wp-product-bundle') < 0) {
      html += '<wp-product-bundle></wp-product-bundle>';
    }
    if (
      this.props.article.buyingGuide &&
      html.indexOf('<wp-buying-guide') < 0 &&
      this.props.article.customContentLayoutTypes.indexOf('buying-guide') >= 0
    ) {
      html += '<wp-buying-guide></wp-buying-guide>';
    }

    html += '</div>';

    return ReactHtmlParser(html, {
      transform: this._transformNodes,
    });
  }

  private _transformNodes = (node: DomElement, index: number, transform: Transform) => {
    if (node.type == 'tag' && node.name == 'wp-gallery' && node.attribs['index']) {
      if (this.props.article.customContentLayoutTypes.indexOf('galleries') < 0) {
        return null;
      }
      const galleryIndex = parseInt(node.attribs['index']) - 1;
      if (galleryIndex >= 0 && galleryIndex < this.props.article.galleries.length) {
        const gallery = this.props.article.galleries[galleryIndex];
        const photos = gallery.photos.map((photo, imageIndex) => ({
          key: imageIndex.toString(),
          src: photo.src,
          width: 2,
          height: imageIndex == 0 || imageIndex > 2 ? 2 : 0.95,
          caption: photo.caption,
          credit: photo.credit,
          alt: photo.caption,
        }));

        return (
          <GalleryWithLightBox
            photos={photos}
            caption={gallery.caption}
            credit={gallery.credit}
            creditLink={gallery.creditLink}
          />
        );
      }
    }
    if (node.type == 'tag' && node.name == 'wp-youtube') {
      return (
        <div className="youtube-wrapper">
          <iframe
            src={node.attribs['url']}
            frameBorder="0"
            allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
          <ArticleComponentCredits
            caption={node.attribs['caption']}
            credit={node.attribs['credit']}
            creditLink={node.attribs['credit-link']}
          />
        </div>
      );
    }
    if (node.type == 'tag' && node.name == 'wp-blockquote') {
      const attribution = node.attribs['attribution'];
      return (
        <div className={`blockquote justify-${node.attribs['justify'] || 'left'}`}>
          <div className="content">
            <p>
              “
              {node.children.map((child: DomElement, childIndex: number) => {
                return <span key={childIndex}>{convertNodeToElement(child, childIndex, transform)}</span>;
              })}
              ”
            </p>
          </div>
          {attribution && (
            <div className="attribution">
              <p>– {attribution}</p>
            </div>
          )}
        </div>
      );
    }
    if (node.type == 'tag' && node.name == 'wp-recipe') {
      if (this.props.article.customContentLayoutTypes.indexOf('recipe') < 0) {
        return null;
      }
      if (this.props.article.recipe) {
        return <RecipeCard recipe={this.props.article.recipe} />;
      }
    }
    if (node.type == 'tag' && node.name == 'wp-product-bundle') {
      return (
        <ProductCarousel
          title={node.attribs['title'] || 'Helpful Products'}
          productLinks={this.props.article.productBundle}
        />
      );
    }

    if (node.type == 'tag' && node.name == 'wp-buying-guide') {
      if (this.props.article.customContentLayoutTypes.indexOf('buying-guide') < 0) {
        return null;
      }
      return <BuyingGuide productLinks={this.props.article.buyingGuide} />;
    }

    /*if (index === 0 && node.type == "tag" && node.name == "p") {
      node.attribs["class"] = ((node.attribs["class"] || "") + " lead").trim();
      return convertNodeToElement(node, index, transform);
    }*/

    return undefined; // use default value
  };
}
